import { FC, useCallback } from "react";
import Stack from "@mui/material/Stack";
import { IconButton, Link, Tooltip } from "@mui/material";
import { EditOutlined, ListAlt } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import LinkIcon from "@mui/icons-material/Link";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { DescriptionPopover } from "./DescriptionPopover";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { Schedule } from "@/services/cloudchipr.api";
import { useHover } from "@/utils/hooks/useHover.hook";
import { useAppAbility } from "@/services/permissions";
import { CopyIconButton } from "@/components/common/CopyIconButton";
import { connectSourceAndExecutedByaFilterValue } from "@/components/pages/execution-log/utils/helpers/getSourceAndExecutedByFromSelectValues";
import { executionLogSourceAndExecutedByFilterKey } from "@/components/pages/execution-log/utils/constants/common";

interface WorkflowNameCellContentProps {
  schedule: Schedule;
  iconSrc: string;
  onScheduleResourcesDrawerOpen(): void;
}

export const WorkflowNameCellContent: FC<WorkflowNameCellContentProps> = ({
  iconSrc,
  schedule,
  onScheduleResourcesDrawerOpen,
}) => {
  const navigate = useNavigate();
  const { ref, hovered } = useHover();

  const { can } = useAppAbility();
  const canEditSchedule = can("edit", "schedule");

  const titleClickHandler = useCallback(() => {
    navigate(`/automations/workflow/edit/${schedule.id}`);
  }, [navigate, schedule.id]);

  return (
    <Stack
      ref={ref}
      direction="row"
      height="100%"
      alignItems="center"
      spacing={0.5}
    >
      <img src={iconSrc} alt={schedule.action} />

      <Stack overflow="hidden" justifyContent="center">
        <Stack direction="row" spacing={1} height={30} alignItems="center">
          <TypographyWithTooltip
            placement="top"
            color="primary"
            sx={typographyStyles}
            title={schedule.name}
            onClick={titleClickHandler}
          />

          <CopyIconButton
            visible={hovered}
            data={schedule.name}
            TooltipProps={{ title: "Copy name", placement: "top" }}
          />

          <CopyIconButton
            visible={hovered}
            data={`${window.location.href}?search=${schedule.name}`}
            TooltipProps={{ title: "Copy URL", placement: "top" }}
          >
            <LinkIcon fontSize="small" />
          </CopyIconButton>

          {hovered && (
            <Tooltip arrow title="See found resources" placement="top">
              <IconButton size="small" onClick={onScheduleResourcesDrawerOpen}>
                <ListAlt fontSize="small" color="action" />
              </IconButton>
            </Tooltip>
          )}

          {hovered && (
            <Tooltip arrow title="See Execution Logs" placement="top">
              <IconButton
                size="small"
                component={Link}
                href={`/execution-log?${executionLogSourceAndExecutedByFilterKey}=${connectSourceAndExecutedByaFilterValue(
                  "workflow",
                  schedule.name,
                )}`}
                target="_blank"
              >
                <DescriptionOutlinedIcon fontSize="small" color="action" />
              </IconButton>
            </Tooltip>
          )}

          {hovered && canEditSchedule && (
            <Tooltip arrow title="Edit" placement="top">
              <IconButton size="small" onClick={titleClickHandler}>
                <EditOutlined fontSize="small" color="action" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        <DescriptionPopover title={schedule.description} />
      </Stack>
    </Stack>
  );
};

const typographyStyles = { textDecoration: "underline", cursor: "pointer" };
